import {
  getCurrentMonthYear,
  applyMetaData,
  getCanonicalDomain
} from './metaUtils'
import { generateFAQPageSchema } from '~/plugins/structuredData/FAQPage'
import { generateEstimatedSalarySchema } from '~/plugins/structuredData/estimatedSalary'

export interface SalaryCalculatorMetaParams {
  role: string | null
  location: string | null
  stateName: string | null
  stateCode: string | null
  metro: string | null
  salary: {
    P10: number
    P25: number
    P50: number
    P75: number
    P90: number
    count: number
  } | null
  faqItems: { question: string; answer: string }[]
  canonicalPath: string
  /** Whether this page should be indexed by search engines (based on search volume) */
  indexable?: boolean
}

/**
 * Formats currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

/**
 * Generates SEO meta data for salary calculator pages
 * Includes FAQ schema and Occupation/EstimatedSalary schema
 */
export function generateSalaryCalculatorPageMeta(params: SalaryCalculatorMetaParams) {
  const { year } = getCurrentMonthYear()
  const { role, location, stateName, stateCode, metro, salary, faqItems, canonicalPath, indexable = false } = params

  // Ensure canonical path is always lowercase
  const normalizedCanonicalPath = canonicalPath.toLowerCase()

  let title: string
  let description: string
  // Homepage and location-only pages are always indexable, role pages need verified search volume
  const isHomepage = !role && !location
  const isLocationOnly = !role && !!location
  const index = isHomepage || isLocationOnly || indexable
  const follow = isHomepage || isLocationOnly || indexable
  const schemas: string[] = []

  // Build location string for display
  const locationDisplay = metro || stateName || 'the United States'

  // Generate title and description based on parameters
  if (role && salary) {
    // Role with salary data
    if (location) {
      title = `${role} Salary in ${locationDisplay} ${year} | Employers.io`
      description = `${role} salary in ${locationDisplay}: median ${formatCurrency(salary.P50)}, ranging from ${formatCurrency(salary.P10)} to ${formatCurrency(salary.P90)}. View salary percentiles, compensation data, and related jobs.`
    } else {
      title = `${role} Salary ${year} | Employers.io`
      description = `${role} salary in the United States: median ${formatCurrency(salary.P50)}, ranging from ${formatCurrency(salary.P10)} to ${formatCurrency(salary.P90)}. View salary percentiles and compensation data.`
    }

    // Add Occupation/EstimatedSalary schema for role-based pages
    const occupationSchema = generateEstimatedSalarySchema({
      occupationName: role,
      description: `Salary information for ${role}${location ? ` in ${locationDisplay}` : ''}`,
      minSalary: salary.P10,
      maxSalary: salary.P90,
      medianSalary: salary.P50,
      percentile10: salary.P10,
      percentile25: salary.P25,
      percentile75: salary.P75,
      percentile90: salary.P90,
      currency: 'USD',
      paymentPeriod: 'P1Y',
      city: metro ? metro.split(',')[0].trim() : undefined,
      state: stateCode || undefined,
      country: 'US',
      url: `${getCanonicalDomain()}${normalizedCanonicalPath}`,
      sampleSize: salary.count,
      lastReviewed: new Date()
    })
    schemas.push(JSON.stringify(occupationSchema))
  } else if (role) {
    // Role without salary data
    if (location) {
      title = `${role} Salary in ${locationDisplay} | Employers.io`
      description = `Find ${role} salary information in ${locationDisplay}. View salary ranges, percentiles, and compensation data for ${role} positions.`
    } else {
      title = `${role} Salary | Employers.io`
      description = `Find ${role} salary information. View salary ranges, percentiles, and compensation data for ${role} positions in the United States.`
    }
  } else if (location && salary) {
    // Location only with salary data
    title = `Best Paying Jobs in ${locationDisplay} ${year} | Employers.io`
    description = `Salaries in ${locationDisplay}: median ${formatCurrency(salary.P50)}, ranging from ${formatCurrency(salary.P10)} to ${formatCurrency(salary.P90)}. Discover the highest paying jobs and career opportunities.`
  } else if (location) {
    // Location only without salary data
    title = `Best Paying Jobs in ${locationDisplay} | Employers.io`
    description = `Discover the best paying jobs in ${locationDisplay}. View top salaries, compensation ranges, and career opportunities in this area.`
  } else {
    // Default (no role or location)
    title = `Salary Calculator - Find Salaries by Role, State & City | Employers.io`
    description = `Calculate and compare salaries by job title, state, and city. Get salary ranges, percentiles, and compensation data for any role in the United States.`
  }

  // Add FAQ schema if FAQ items exist
  if (faqItems && faqItems.length > 0) {
    const faqSchema = generateFAQPageSchema(faqItems, `${getCanonicalDomain()}${normalizedCanonicalPath}`)
    if (faqSchema) {
      schemas.push(JSON.stringify(faqSchema))
    }
  }

  // Apply meta data using the shared utility
  applyMetaData({
    title,
    description,
    canonical: normalizedCanonicalPath,
    index,
    follow,
    schemas
  })
}
